import { useEffect } from 'react';
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import routes from "./router/config";

import ErrorBoundary from "./components/common/ErrorBoundary";
import { useSiteConfig } from "./hooks/useSiteConfig";
import { useReferralCapture } from "./hooks/useReferralCapture";
import { useUiStore } from "./stores/uiStore";
import { applyRouteMeta, resolveDocumentTitle } from "./seo/meta";

// 邀请码全局捕获桥接组件：不渲染任何 UI，仅承载 ?ref= 捕获/绑定副作用。
// 独立成组件以隔离 authStore 订阅引起的重渲染，避免波及 RouterProvider。
function ReferralCapture() {
  useReferralCapture();
  return null;
}

const router = createBrowserRouter(routes);

function App() {
  const applyAppearance = useUiStore((s) => s.applyAppearance);
  const siteConfig = useSiteConfig();

  // 应用程序初始化时应用外观设置到 DOM
  useEffect(() => {
    applyAppearance();
  }, [applyAppearance]);

  // 页面标题与 favicon 随后台「站点信息」配置动态更新
  // 分享页除外：那是对外公开页，标签上印 ICP 备案网站名既冗长又与页内品牌标识不一致，
  // 标题由 `pages/share-order` 自己定（见该页 `SHARE_PAGE_TITLE`）。
  // 工作台路由的标题沿用原行为（只写站点名），只有命中 seo/pages.ts 公开页清单的路径才加页面名前缀。
  useEffect(() => {
    const apply = () => {
      const pathname = router.state.location.pathname;
      applyRouteMeta(pathname, siteConfig.siteName);
      if (pathname.startsWith('/share/')) return;
      if (siteConfig.siteName) {
        document.title = resolveDocumentTitle(pathname, siteConfig.siteName);
      }
    };
    apply();
    // SPA 跳转不重新加载文档，收录指令与规范地址必须跟着路由走；router 是模块级单例，退订放进 cleanup
    return router.subscribe(apply);
  }, [siteConfig.siteName]);

  useEffect(() => {
    if (!siteConfig.faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = siteConfig.faviconUrl;
  }, [siteConfig.faviconUrl]);

  return (
    <ErrorBoundary>
      <div className="min-h-screen">
        <ReferralCapture />
        <RouterProvider router={router} />
      </div>
    </ErrorBoundary>
  );
}

export default App;
